import * as THREE from 'three';

export interface RaycastResult {
  object: THREE.Object3D;
  point: THREE.Vector3;
  distance: number;
  face?: THREE.Face;
}

export class SelectionManager {
  private raycaster: THREE.Raycaster;
  private camera: THREE.Camera;
  private scene: THREE.Scene;
  
  // Performance optimization
  private lastRaycastTime: number = 0;
  private raycastThrottle: number;
  
  // Current state
  private hoveredObject: THREE.Object3D | null = null;
  private interactiveObjects: Set<THREE.Object3D> = new Set();
  
  // Pointer tracking
  private pointer: THREE.Vector2 = new THREE.Vector2();
  private lastPointer: THREE.Vector2 = new THREE.Vector2();
  
  constructor(
    camera: THREE.Camera,
    scene: THREE.Scene,
    raycastThrottle: number = 33
  ) {
    this.camera = camera;
    this.scene = scene;
    this.raycastThrottle = raycastThrottle;
    
    this.raycaster = new THREE.Raycaster();
    // Optimize raycaster for performance
    this.raycaster.params.Points.threshold = 0.1;
    this.raycaster.params.Line.threshold = 1;
    
    this.updateInteractiveObjects();
  }
  
  public updatePointer(x: number, y: number, canvas: HTMLCanvasElement): void {
    // Convert screen coordinates to normalized device coordinates (-1 to +1)
    const rect = canvas.getBoundingClientRect();
    this.pointer.x = ((x - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((y - rect.top) / rect.height) * 2 + 1;
  }
  
  public performRaycast(): RaycastResult | null {
    const now = Date.now();
    
    // Throttle raycasting for performance
    if (now - this.lastRaycastTime < this.raycastThrottle) {
      return null;
    }
    
    this.lastRaycastTime = now;
    
    // Update raycaster
    this.raycaster.setFromCamera(this.pointer, this.camera);
    
    // Get interactive objects to test against
    const objects = this.getInteractiveObjects();
    if (objects.length === 0) return null;
    
    // Perform raycast
    const intersects = this.raycaster.intersectObjects(objects, true);
    
    if (intersects.length > 0) {
      const intersection = intersects[0];
      return {
        object: intersection.object,
        point: intersection.point,
        distance: intersection.distance,
        face: intersection.face,
      };
    }
    
    return null;
  }
  
  public updateHover(): THREE.Object3D | null {
    const result = this.performRaycast();
    const newHoveredObject = result?.object || null;
    
    // Check if hover state changed
    if (newHoveredObject !== this.hoveredObject) {
      // Clear previous hover
      if (this.hoveredObject) {
        this.clearHoverEffect(this.hoveredObject);
      }
      
      // Set new hover
      this.hoveredObject = newHoveredObject;
      if (this.hoveredObject) {
        this.applyHoverEffect(this.hoveredObject);
      }
    }
    
    return this.hoveredObject;
  }
  
  public getClickTarget(): THREE.Object3D | null {
    const result = this.performRaycast();
    return result?.object || null;
  }
  
  public getHoveredObject(): THREE.Object3D | null {
    return this.hoveredObject;
  }
  
  private getInteractiveObjects(): THREE.Object3D[] {
    // Update interactive objects list periodically
    const objects: THREE.Object3D[] = [];
    
    this.scene.traverse((object) => {
      // Check if object is interactive
      if (this.isInteractive(object)) {
        objects.push(object);
      }
    });
    
    return objects;
  }
  
  private isInteractive(object: THREE.Object3D): boolean {
    // An object is interactive if:
    // 1. It has a mesh (is visible/renderable)
    // 2. It has userData with an ID
    // 3. It's not background/environment (like starfield)
    
    if (!(object instanceof THREE.Mesh)) return false;
    
    const userData = object.userData;
    if (!userData['id']) return false;
    
    // Exclude background objects
    if (userData['type'] === 'background' || userData['type'] === 'environment') {
      return false;
    }
    
    // Include ships, planets, mining collectors, and other interactive elements
    return userData['type'] === 'ship' || 
           userData['type'] === 'planet' || 
           userData['type'] === 'block' ||
           userData['type'] === 'mining-collector' ||
           userData['interactive'] === true;
  }
  
  private applyHoverEffect(object: THREE.Object3D): void {
    // Store original material properties for restoration
    if (object instanceof THREE.Mesh && object.material) {
      const material = object.material as THREE.Material;
      if ('emissive' in material) {
        const emissiveMaterial = material as THREE.MeshStandardMaterial;
        
        // Store original emissive properties if not already stored
        if (!object.userData['originalEmissive']) {
          object.userData['originalEmissive'] = emissiveMaterial.emissive.clone();
          object.userData['originalEmissiveIntensity'] = emissiveMaterial.emissiveIntensity;
        }
        
        // Apply hover effect by adding to the existing emissive
        const hoverColor = new THREE.Color(0x444444);
        const currentEmissive = object.userData['originalEmissive'].clone();
        currentEmissive.add(hoverColor);
        
        emissiveMaterial.emissive = currentEmissive;
        emissiveMaterial.emissiveIntensity = Math.min(1.0, object.userData['originalEmissiveIntensity'] + 0.3);
      }
    }
  }
  
  private clearHoverEffect(object: THREE.Object3D): void {
    // Restore original emissive properties
    if (object instanceof THREE.Mesh && object.material) {
      const material = object.material as THREE.Material;
      if ('emissive' in material && object.userData['originalEmissive']) {
        const emissiveMaterial = material as THREE.MeshStandardMaterial;
        
        // Restore the original emissive color and intensity
        emissiveMaterial.emissive = object.userData['originalEmissive'].clone();
        emissiveMaterial.emissiveIntensity = object.userData['originalEmissiveIntensity'];
      }
    }
  }
  
  public clearHover(): void {
    if (this.hoveredObject) {
      this.clearHoverEffect(this.hoveredObject);
      this.hoveredObject = null;
    }
  }
  
  /**
   * Reset all mining collectors that may have been corrupted by the old hover system
   */
  public resetMiningCollectorMaterials(): void {
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh && 
          object.userData['type'] === 'mining-collector' &&
          object.userData['collectorData']) {
        
        const collectorData = object.userData['collectorData'];
        const material = object.material as THREE.MeshStandardMaterial;
        
        // Reset to original color and emissive properties
        const originalColor = new THREE.Color(collectorData.poolName === 'AntPool' ? 0xff4444 :
                                              collectorData.poolName === 'Foundry USA' ? 0x44ff44 :
                                              collectorData.poolName === 'F2Pool' ? 0x4444ff :
                                              collectorData.poolName === 'Binance Pool' ? 0xffaa44 :
                                              collectorData.poolName === 'ViaBTC' ? 0xaa44ff :
                                              collectorData.poolName === 'Poolin' ? 0x44aaff : 0xffffff);
        
        material.color = originalColor;
        material.emissive = originalColor;
        material.emissiveIntensity = 0.5;
        
        // Clear any stored hover data
        delete object.userData['originalEmissive'];
        delete object.userData['originalEmissiveIntensity'];
        delete object.userData['originalMaterial'];
        
        console.log(`Reset material for mining collector: ${collectorData.poolName}`);
      }
    });
  }
  
  private updateInteractiveObjects(): void {
    // This could be optimized further with spatial partitioning
    // For now, we'll update on each raycast
  }
  
  public update(_deltaTime: number): void {
    // Periodic cleanup and optimization
    // Could add spatial partitioning updates here
  }
  
  public dispose(): void {
    this.clearHover();
    this.interactiveObjects.clear();
  }
}